/* Exo 2.0 v3.0.0. Reuses CartDrawer section rendering; no alternate cart UI. */
(() => {
  'use strict';
  const tag = 'mp-immersive-carousel';
  if (customElements.get(tag)) return;
  let cartBusy = false;
  let cartUncertain = false;

  class MPImmersiveCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initFrame || this.controller) return;
      this.initFrame = requestAnimationFrame(() => {
        this.initFrame = 0;
        this.init();
      });
    }

    init() {
      if (this.controller || !this.isConnected) return;
      this.stage = this.querySelector('[data-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.stage || !this.slides.length) return;
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.counter = this.querySelector('[data-counter]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.feedback = this.querySelector('[data-feedback]');
      this.announcer = this.querySelector('[data-announcer]');
      this.recovery = this.querySelector('[data-recovery]');
      this.backgroundSlot = this.querySelector('[data-background-slot]');
      this.quickDialog = this.querySelector('[data-quick-dialog]');
      this.quickContent = this.querySelector('[data-quick-content]');
      if (this.slides.length === 2) {
        this.mirror = document.createElement('button');
        this.mirror.type = 'button';
        this.mirror.className = 'mp-immersive__slide';
        this.mirror.setAttribute('data-mirror', '');
        this.mirror.setAttribute('aria-hidden', 'true');
        this.mirror.tabIndex = -1;
        this.stage.append(this.mirror);
      }
      this.backgroundCache = new Map();
      this.controller = new AbortController();
      this.active = Math.max(0, Math.min(Number(this.dataset.initial) || 0, this.slides.length - 1));
      this.busy = false;
      this.pointer = null;
      this.suppressUntil = 0;
      this.visible = false;
      this.focusTargets = this.panels.map(panel => Array.from(panel.querySelectorAll('a, button, input, select, textarea, [tabindex]')).map(element => ({
        element, original: element.getAttribute('tabindex')
      })));
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('keydown', event => this.onKey(event), options);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-quick-variant]')) return;
        const option = event.target.selectedOptions[0];
        const form = event.target.closest('form');
        form.querySelector('[name="id"]').value = option.value;
        const button = form.querySelector('[data-submit]');
        button.textContent = button.dataset.label + ' — ' + option.dataset.price;
        this.showQuickPhoto(option.dataset.imageId);
      }, options);
      this.quickDialog.addEventListener('click', event => {
        if (event.target === this.quickDialog) {
          const rect = this.quickDialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeQuick();
        }
      }, options);
      this.quickDialog.addEventListener('cancel', event => { event.preventDefault(); this.closeQuick(); }, options);
      this.stage.addEventListener('pointerdown', event => this.onPointerDown(event), options);
      this.stage.addEventListener('pointermove', event => this.onPointerMove(event), options);
      this.stage.addEventListener('pointerup', event => this.onPointerEnd(event), options);
      this.stage.addEventListener('pointercancel', () => this.cancelPointer(), options);
      this.stage.addEventListener('lostpointercapture', event => {
        // Touch starts with implicit capture on the image button. Transferring
        // it to the stage must not be mistaken for an interrupted gesture.
        if (event.target === this.stage && this.pointer?.id === event.pointerId) this.cancelPointer();
      }, options);
      this.stage.addEventListener('pointerleave', event => {
        if (this.pointer && !this.pointer.locked && event.pointerType === 'mouse') this.cancelPointer();
      }, options);
      this.stage.addEventListener('wheel', event => {
        if (event.ctrlKey || this.busy || this.quickDialog.open || this.slides.length < 2) return;
        const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (!horizontal && !event.shiftKey) return;
        event.preventDefault();
        const now = performance.now();
        if (now - (this.wheelAt || 0) < 380) return;
        if (now - (this.wheelInputAt || 0) > 180) this.wheelDelta = 0;
        this.wheelInputAt = now;
        const delta = (horizontal ? event.deltaX : event.deltaY) * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.width : 1);
        if (Math.sign(delta) !== Math.sign(this.wheelDelta)) this.wheelDelta = 0;
        this.wheelDelta = (this.wheelDelta || 0) + delta;
        if (Math.abs(this.wheelDelta) >= 45) { this.select(this.active + Math.sign(this.wheelDelta)); this.wheelAt = now; this.wheelDelta = 0; }
      }, { ...options, passive: false });
      this.stage.addEventListener('dragstart', event => event.preventDefault(), options);
      window.addEventListener('blur', () => this.cancelPointer(), options);
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index !== -1) this.select(index, false);
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        this.cancelPointer();
        this.measure();
        this.paint();
      });
      this.resizeObserver.observe(this.stage);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.dataset.visible = String(this.visible);
        if (!this.visible) this.cancelPointer();
        else this.showBackground();
      });
      this.intersectionObserver.observe(this);
      this.querySelectorAll('[data-direction], [data-dot], [data-slide]').forEach(button => { button.disabled = false; });
      this.measure();
      this.select(this.active, false);
      this.dataset.ready = 'true';
    }

    measure() {
      this.mobile = window.matchMedia('(max-width: 749px)').matches;
      this.width = this.stage.getBoundingClientRect().width;
      this.radius = this.mobile ? 1 : Math.min(2, Math.floor((this.slides.length - 1) / 2));
      if (this.slides.length === 2) this.radius = 1;
      this.step = this.mobile ? this.width * 0.43 : this.width / (this.radius * 2 + 1.6);
    }

    distance(index) {
      const count = this.slides.length;
      let offset = (index - this.active + count) % count;
      if (offset > Math.ceil((count - 1) / 2)) offset -= count;
      return offset;
    }

    paint(delta = 0) {
      if (!this.stage) return;
      const fraction = this.step ? delta / this.step : 0;
      const paintSlide = (slide, offset) => {
        const oldOffset = Number(slide.dataset.offset);
        const wraps = Number.isFinite(oldOffset) && Math.abs(oldOffset - offset) > 1;
        if (wraps) slide.classList.add('mp-immersive__reposition');
        slide.dataset.offset = String(offset);
        const position = offset + fraction;
        const distance = Math.abs(position);
        const hidden = Math.abs(offset) > this.radius;
        slide.style.setProperty('--mp-x', (position * this.step).toFixed(2) + 'px');
        slide.style.setProperty('--mp-y', Math.min(distance * 8, 20).toFixed(2) + 'px');
        slide.style.setProperty('--mp-angle', (Math.max(-1, Math.min(1, position)) * 11).toFixed(2) + 'deg');
        slide.style.setProperty('--mp-scale', String(Math.max(0.55, 1 - distance * 0.30)));
        slide.style.setProperty('--mp-opacity', hidden ? '0' : String(1 - Math.min(distance, 1) * 0.22));
        slide.style.setProperty('--mp-z', String(10 - Math.min(6, Math.round(distance))));
        slide.dataset.offscreen = String(hidden);
        slide.setAttribute('aria-hidden', String(hidden));
        slide.tabIndex = hidden ? -1 : 0;
        if (wraps) { void slide.offsetWidth; slide.classList.remove('mp-immersive__reposition'); }
      };
      this.slides.forEach((slide, index) => paintSlide(slide, this.distance(index)));
      if (this.mirror) {
        const index = 1 - this.active;
        if (this.mirror.dataset.index !== String(index)) {
          this.mirror.replaceChildren(...Array.from(this.slides[index].childNodes, node => node.cloneNode(true)));
          this.mirror.dataset.index = String(index);
        }
        paintSlide(this.mirror, -1);
        this.mirror.setAttribute('aria-hidden', 'true');
        this.mirror.tabIndex = -1;
      }
    }

    select(index, announce = true) {
      if (!this.slides.length || this.busy) return;
      if (this.quickDialog?.open) this.closeQuick();
      this.cancelPointer();
      const next = (index + this.slides.length) % this.slides.length;
      const changed = next !== this.active;
      const previousPanel = this.panels[this.active];
      if (changed && previousPanel?.contains(document.activeElement)) {
        this.stage.focus({ preventScroll: true });
      }
      this.active = next;
      this.dataset.active = String(next);
      if (this.counter) this.counter.textContent = (next + 1) + ' / ' + this.slides.length;
      this.style.setProperty('--mp-shade', this.slides[next].dataset.accent || '#ee009c');
      this.slides.forEach((slide, i) => slide.setAttribute('aria-current', String(i === next)));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === next)));
      this.panels.forEach((panel, i) => {
        const active = i === next;
        panel.setAttribute('aria-hidden', String(!active));
        panel.toggleAttribute('inert', !active);
        // Explicit tab order fallback for Safari versions predating inert.
        this.focusTargets[i].forEach(({ element, original }) => {
          if (!active) element.setAttribute('tabindex', '-1');
          else if (original === null) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', original);
        });
      });
      this.paint();
      if (this.visible) this.showBackground();
      if (announce && changed) {
        const panel = this.panels[next];
        const price = panel.dataset.price || '';
        this.announcer.textContent = [this.slides[next].dataset.name, price, (next + 1) + ' of ' + this.slides.length].filter(Boolean).join('. ');
      }
    }

    showBackground() {
      const template = this.panels[this.active].querySelector('[data-background-template]');
      const key = template ? String(this.active) : 'base';
      if (this.backgroundSlot.dataset.key === key) return;
      this.backgroundSlot.replaceChildren();
      this.backgroundSlot.dataset.key = key;
      if (!template) return;
      let image = this.backgroundCache.get(key);
      if (!image) {
        image = template.content.firstElementChild.cloneNode(true);
        image.addEventListener('error', () => { image.hidden = true; }, { once: true });
        this.backgroundCache.set(key, image);
      }
      this.backgroundSlot.append(image);
    }

    showQuick(opener) {
      if (this.busy || typeof this.quickDialog.showModal !== 'function') return;
      const panel = this.panels[this.active];
      const template = panel.querySelector('[data-quick-template]');
      if (!template) return;
      this.cancelPointer();
      this.quickOpener = opener;
      this.quickContent.replaceChildren(template.content.cloneNode(true));
      this.quickContent.style.cssText = panel.style.cssText;
      this.querySelector('[data-quick-feedback]').textContent = '';
      this.querySelector('[data-quick-cart]').hidden = true;
      this.quickDialog.setAttribute('aria-label', this.quickContent.querySelector('[data-quick-title]')?.textContent + ' — Quick View');
      this.quickDialog.showModal();
      this.querySelector('[data-quick-scroll]').scrollTop = 0;
    }

    showQuickPhoto(id) {
      if (!id) return;
      const template = Array.from(this.quickContent.querySelectorAll('[data-quick-photo]')).find(el => el.dataset.quickPhoto === id);
      if (!template) return;
      this.quickContent.querySelector('[data-quick-main]').replaceChildren(template.content.cloneNode(true));
      this.quickContent.querySelectorAll('[data-quick-thumb]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.quickThumb === id)));
    }

    closeQuick(restoreFocus = true) {
      if (!this.quickDialog?.open) return;
      this.quickDialog.close();
      if (restoreFocus && this.quickOpener?.isConnected) this.quickOpener.focus({ preventScroll: true });
    }

    onClick(event) {
      const photo = event.target.closest('[data-quick-thumb]');
      if (photo) { this.showQuickPhoto(photo.dataset.quickThumb); return; }
      const opener = event.target.closest('[data-quick-open]');
      if (opener) { this.showQuick(opener); return; }
      if (event.target.closest('[data-quick-close]')) { this.closeQuick(); return; }
      const control = event.target.closest('[data-direction], [data-dot], [data-slide], [data-mirror]');
      if (!control || !this.contains(control)) return;
      if (event.detail > 0 && performance.now() < this.suppressUntil) {
        event.preventDefault();
        return;
      }
      if (control.hasAttribute('data-direction')) this.select(this.active + Number(control.dataset.direction));
      else if (control.hasAttribute('data-dot')) this.select(Number(control.dataset.dot));
      else this.select(Number(control.dataset.index));
    }

    onKey(event) {
      if (!event.target.closest('[data-stage], [data-dot]') || event.altKey || event.ctrlKey || event.metaKey) return;
      let next;
      if (event.key === 'ArrowRight') next = this.active + 1;
      if (event.key === 'ArrowLeft') next = this.active - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.slides.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      // Keep focus on a stable control if the old slide will move out of view.
      if (event.target.closest('[data-slide]')) this.stage.focus({ preventScroll: true });
      this.select(next);
    }

    onPointerDown(event) {
      if (this.pointer) { this.cancelPointer(); return; }
      if (this.quickDialog?.open || !this.visible || this.busy || this.slides.length < 2 || !event.isPrimary || event.button !== 0) return;
      if (event.target.closest('[data-direction]')) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, locked: false };
    }

    onPointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.locked) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 9) return;
        if (Math.abs(dy) >= Math.abs(dx)) { this.cancelPointer(); return; }
        pointer.locked = true;
        this.stage.setPointerCapture(event.pointerId);
        this.dataset.dragging = 'true';
      }
      pointer.dx = dx;
      if (!this.moveFrame) this.moveFrame = requestAnimationFrame(() => {
        this.moveFrame = 0;
        if (this.pointer) this.paint(Math.max(-this.step, Math.min(this.step, this.pointer.dx)));
      });
    }

    onPointerEnd(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const moved = pointer.locked && Math.abs(pointer.dx) >= Math.min(65, this.step * 0.3);
      const direction = pointer.dx < 0 ? 1 : -1;
      if (pointer.locked) this.suppressUntil = performance.now() + 350;
      this.cancelPointer();
      if (moved) this.select(this.active + direction);
    }

    cancelPointer() {
      const pointer = this.pointer;
      this.pointer = null;
      if (this.moveFrame) cancelAnimationFrame(this.moveFrame);
      this.moveFrame = 0;
      this.dataset.dragging = 'false';
      if (pointer && this.stage?.hasPointerCapture(pointer.id)) this.stage.releasePointerCapture(pointer.id);
      if (this.stage && this.slides) this.paint();
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-submit]:not([data-unavailable]), [data-direction], [data-dot], [data-slide], [data-mirror], [data-quick-open], [data-quick-variant]').forEach(button => {
        button.disabled = busy;
      });
    }

    message(text, recovery = false) {
      this.feedback.textContent = text;
      this.recovery.hidden = !recovery;
      if (this.quickDialog?.open) {
        this.querySelector('[data-quick-feedback]').textContent = text;
        this.querySelector('[data-quick-cart]').hidden = !recovery;
      }
    }

    validSections(drawer, sections) {
      return drawer.getSectionsToRender().every(section => {
        const html = sections?.[section.section];
        return typeof html === 'string' && document.querySelector(section.id) &&
          new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      });
    }

    async onSubmit(event) {
      const form = event.target;
      if (!form.matches('.mp-immersive__form')) return;
      if (this.busy || cartBusy || cartUncertain) {
        event.preventDefault();
        if (cartUncertain) this.message('Please check your cart before adding another item.', true);
        else if (!this.busy) this.message('Another item is being added. Please wait.');
        return;
      }
      const fromQuick = this.quickDialog.contains(form);
      if (!fromQuick && form.closest('[data-panel]') !== this.panels[this.active]) {
        event.preventDefault();
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
        // Preserve Exo's standard full-page product form when its drawer is absent.
        cartBusy = true;
        this.setBusy(true);
        return;
      }
      event.preventDefault();
      const submit = form.querySelector('[data-submit]');
      const label = submit.textContent;
      const name = this.slides[this.active].dataset.name;
      const lifecycle = this.controller;
      const sectionNames = drawer.getSectionsToRender().map(section => section.section);
      const formData = new FormData(form);
      formData.set('sections', sectionNames.join(','));
      formData.set('sections_url', window.location.pathname + window.location.search);
      cartBusy = true;
      this.cancelPointer();
      this.setBusy(true);
      submit.textContent = 'Adding…';
      this.message('');
      let added = false;
      let definitiveFailure = false;
      try {
        const response = await fetch(window.routes?.cart_add_url || form.action, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          // Shopify validation failures are safe to correct and retry.
          definitiveFailure = response.status >= 400 && response.status < 500;
          const detail = typeof result.description === 'string' ? result.description :
            (typeof result.message === 'string' ? result.message : 'This item could not be added.');
          throw new Error(detail);
        }
        added = true;
        if (!this.validSections(drawer, result.sections)) {
          const refreshURL = new URL(window.location.href);
          refreshURL.searchParams.delete('section_id');
          refreshURL.searchParams.set('sections', sectionNames.join(','));
          const refreshed = await fetch(refreshURL, { headers: { Accept: 'application/json' } });
          if (!refreshed.ok) throw new Error('Cart refresh failed.');
          result.sections = await refreshed.json();
        }
        if (!this.validSections(drawer, result.sections)) throw new Error('Cart refresh failed.');
        if (!this.isConnected || this.controller !== lifecycle) return;
        if (fromQuick) this.closeQuick();
        drawer.setActiveElement?.(fromQuick ? this.quickOpener : submit);
        drawer.renderContents(result);
        this.message(name + ' added to your cart.');
      } catch (error) {
        if (!definitiveFailure) cartUncertain = true;
        if (!this.isConnected || this.controller !== lifecycle) return;
        if (added) {
          this.message('Added to your cart, but the drawer could not refresh. View your cart before adding again.', true);
        } else if (definitiveFailure) {
          this.message(error.message);
        } else {
          this.message('We could not confirm whether this item was added. Check your cart before trying again.', true);
        }
      } finally {
        cartBusy = false;
        if (this.isConnected && this.controller === lifecycle) {
          this.setBusy(false);
          submit.textContent = label;
        }
      }
    }

    destroy() {
      if (this.initFrame) cancelAnimationFrame(this.initFrame);
      this.initFrame = 0;
      this.cancelPointer();
      this.closeQuick(false);
      this.mirror?.remove();
      this.mirror = null;
      this.controller?.abort();
      this.controller = null;
      this.focusTargets?.flat().forEach(({ element, original }) => {
        if (original === null) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', original);
      });
      this.focusTargets = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.backgroundCache?.clear();
      delete this.dataset.ready;
    }

    disconnectedCallback() { this.destroy(); }
  }

  customElements.define(tag, MPImmersiveCarousel);
  // One lifecycle bridge, regardless of how many section script tags Shopify renders.
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll?.(tag).forEach(section => section.connectedCallback());
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll?.(tag).forEach(section => section.destroy());
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll(tag).forEach(section => section.cancelPointer());
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    cartBusy = false;
    document.querySelectorAll(tag).forEach(section => {
      if (section.controller) section.setBusy(false);
    });
  });
})();
